'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { RelatedProducts } from '@/types/product_page/productType';
import RelatedProductCard from './related_product_card';
import useMediaQuery from '@/hooks/useMediaQuery';

const Accessories = ({
  heading,
  data,
  popup,
  show,
}: {
  heading: string;
  data: RelatedProducts[];
  popup?: boolean;
  show?: boolean;
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);

  const firstButtonRef = useRef<any>(null);
  const lineRef = useRef<any>(null);

  const isMobile = useMediaQuery('(max-width: 767px)');

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleButtonClick = (index: number, event: any) => {
    setActiveIndex(index);
    const lineElement = lineRef.current;
    if (lineElement) {
      const buttonRect = event.target.getBoundingClientRect();
      const lineLeft =
        buttonRect.left -
        lineElement.parentElement.getBoundingClientRect().left;
      lineElement.style.transform = `translateX(${lineLeft}px)`;
      lineElement.style.width = `${buttonRect.width}px`;
    }
  };

  useEffect(() => {
    if (firstButtonRef.current) {
      firstButtonRef.current.click();
    }
  }, [data]);

  const uniqueProductIds = new Set<string>();
  const allProducts = data
    ?.flatMap((tab) => tab?.related_products_translations_id?.variants)
    .filter((product) => {
      if (uniqueProductIds?.has(product?.product_variant_translations_id?.id)) {
        return false;
      } else {
        uniqueProductIds?.add(product?.product_variant_translations_id?.id);
        return true;
      }
    });

  if (!data) return null;

  return (
    <section id="accessories" className="w-full pt-10 md:pb-10 px-4 lg:px-6">
      <h1
        className={`text-3xl ${popup ? 'lg:text-3xl text-[24px]' : ''} font-bold text-center   ${popup ? 'mb-2' : 'mb-6'} `}
      >
        {heading ?? 'Related Products'}
      </h1>
      <div className="flex w-full font-proxima_font">
        <div className="flex w-full md:w-auto items-center gap-4 lg:gap-6 md:mx-auto md:mb-6">
          {isMobile && !popup ? (
            // Dropdown for mobile devices
            <div className="relative w-full md:hidden max-w-desktop">
              <button
                type="button"
                onClick={toggleDropdown}
                className={cn(
                  'bg-gray-50 border flex items-center justify-between border-border-gray text-black font-semibold rounded-full w-full px-4 py-3 focus:outline-none',
                  isOpen && 'border-black'
                )}
              >
                {activeIndex === data?.length + 1
                  ? 'View All'
                  : data[activeIndex]?.related_products_translations_id
                      ?.tab_heading}
                <Image
                  alt="filter"
                  width={200}
                  height={200}
                  src={'/assets/filter/down.svg'}
                  className="h-2  w-auto"
                />
              </button>

              {isOpen && (
                <div
                  style={{ top: '100%' }}
                  className="absolute left-0 w-full z-20 border border-border-gray overflow-hidden  rounded-lg bg-white shadow mt-2 p-2"
                >
                  {data?.map((item, index: number) => (
                    <div
                      key={index}
                      onClick={() => {
                        setActiveIndex(index);
                        setIsOpen(false);
                      }}
                      className={`py-3 px-4 cursor-pointer rounded text-left font-semibold  ${activeIndex === index ? 'bg-off-white text-black font-bold' : 'text-off-black'}`}
                    >
                      {item?.related_products_translations_id?.tab_heading}
                    </div>
                  ))}
                  <div
                    onClick={() => {
                      setActiveIndex(data?.length + 1);
                      setIsOpen(false);
                    }}
                    className={`py-3 px-4 cursor-pointer rounded text-left font-semibold ${activeIndex === data?.length + 1 ? 'bg-off-white text-black font-bold' : 'text-off-black'}`}
                  >
                    View All
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Slide animation for larger screens
            <div className="relative gap-8 border-b border-black/20 mx-auto flex items-center justify-between flex-shrink-0 font-proxima_font">
              <div
                ref={lineRef}
                className="absolute -bottom-[1.2px] h-[3px] bg-black rounded-full"
                style={{
                  transition:
                    'width 0.3s ease-in-out, transform 0.3s ease-in-out',
                }}
              ></div>
              {data?.length > 0 &&
                data?.map((item, index) => (
                  <div
                    key={index}
                    ref={index === 0 ? firstButtonRef : null}
                    onClick={(event) => handleButtonClick(index, event)}
                    className={`py-1 transition-all duration-500 font-semibold ${
                      activeIndex === index ? 'text-black' : 'text-off-black'
                    } cursor-pointer text-sm lg:text-base first:ml-auto last:mr-auto whitespace-nowrap`}
                  >
                    {item?.related_products_translations_id?.tab_heading}
                  </div>
                ))}
              <div
                onClick={(event) => handleButtonClick(data?.length + 1, event)}
                className={`py-1 transition-all duration-500 font-semibold ${
                  activeIndex === data?.length + 1
                    ? 'text-black'
                    : 'text-off-black'
                } cursor-pointer text-sm lg:text-base first:ml-auto last:mr-auto whitespace-nowrap`}
              >
                View All
              </div>
            </div>
          )}
        </div>
      </div>
      {!popup && (
        <div
          className={`max-w-desktop py-6 w-full mx-auto -z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6`}
        >
          {(activeIndex === data?.length + 1
            ? allProducts
            : data[activeIndex]?.related_products_translations_id?.variants
          )?.map((product) => (
            <RelatedProductCard
              key={product?.product_variant_translations_id?.id}
              slug={product?.product_variant_translations_id?.product?.handle}
              image={
                product?.product_variant_translations_id?.gallery_items[0]
                  ?.layout_asset_translations_id
              }
              title={product?.product_variant_translations_id?.title}
              sku={product?.product_variant_translations_id?.sku}
              price={product?.product_variant_translations_id?.rrp}
              points={
                product?.product_variant_translations_id?.product
                  ?.product_card_points
              }
            />
          ))}
        </div>
      )}

      {/* {popup && (
        <div
          className={`max-w-desktop py-3 w-full mx-auto  items-center -z-10 grid md:grid-cols-3 grid-cols-1 gap-[20px]  `}
        >
          {(activeIndex === -1
            ? allProducts
            : data[activeIndex]?.products
          )?.map((product, index) => (
            <AccessoryCard
              key={product?.products_id?.id}
              data={product?.products_id}
            />
          ))}
        </div>
      )} */}
    </section>
  );
};

export default Accessories;
